import { Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TenantService } from './core/services/tenant.service';
import { BusinessHour } from './core/models/business-hour.model';
import { filter, Subscription } from 'rxjs';
import { AnalyticsService } from './core/services/analytics.service';
import { TenantConfig } from './core/models/TenantConfig.model';
import { environment } from '../environments/environment';
import { Title } from '@angular/platform-browser';
import { GtmService } from './core/services/gtm.service';
import { MenuService } from './features/user/menu.service';
import {
  BusinessHoursCalculatorService,
  BusinessHoursMeta,
  DayHours,
} from './core/services/business-hours-calculator.service';
import { HtmlEntityDecoderService } from './shared/services/html-entity-decoder.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  restaurantName = 'Restaurant';
  restaurantAddress = '';
  restaurantPhone = '';
  allergensNote = '';
  openingHoursWeekly: DayHours[] = [];
  deliveryHoursWeekly: DayHours[] = [];
  fees: { minOrder: number; deliveryFee: number; serviceFee: number } = {
    minOrder: 0,
    deliveryFee: 0,
    serviceFee: 0,
  };

  private tenantName = 'Waitron';

  private businessHoursMeta: BusinessHoursMeta | null = null;
  businessHours: BusinessHour[] = [];
  showCart: boolean = true;
  cartOpen: boolean = false;
  cartVisible: boolean = true;

  constructor(
    private router: Router,
    private tenantService: TenantService,
    private analytics: AnalyticsService,
    private title: Title,
    private gtm: GtmService,
    private menuService: MenuService,
    private businessHoursCalc: BusinessHoursCalculatorService,
    private htmlDecoder: HtmlEntityDecoderService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.showCart = !event.url.startsWith('/admin');
      }
    });
  }

  ngOnInit() {
    if (environment.gtmId) {
      this.gtm.load(environment.gtmId, 'dataLayer', environment.gtmEnv);
    }

    // start analytics context + SPA page_view tracking
    this.analytics.init('', {
      tenant_name: window.location.hostname,
      tenant_domain: window.location.hostname,
    });

    // send first page_view
    this.analytics.pageView(this.router.url);

    this.setTitle();

    this.tenantService.businessHoursMeta$.subscribe((meta) => {
      this.businessHoursMeta = meta;
    });

    this.tenantService.businessHours$.subscribe((hours) => {
      this.businessHours = hours ?? [];

      const timeZone = this.businessHoursCalc.getTimezone(
        this.businessHoursMeta,
      );
      const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

      this.openingHoursWeekly =
        this.businessHoursCalc.toDayHoursFromBusinessHours(
          this.businessHours,
          'store',
          timeZone,
          now,
        );

      this.deliveryHoursWeekly =
        this.businessHoursCalc.toDayHoursFromBusinessHours(
          this.businessHours,
          'delivery',
          timeZone,
          now,
        );
    });

    // When tenant config arrives later, just enrich the context
    this.tenantService.tenantConfig$
      .pipe(filter((cfg): cfg is TenantConfig => !!cfg))
      .subscribe((cfg) => {
        const companyName = this.htmlDecoder.decode(
          cfg.tenant?.companyName || '',
        );

        this.tenantName = companyName || 'Waitron';
        this.restaurantName = companyName || 'Restaurant';
        this.restaurantAddress = this.htmlDecoder.decode(
          this.tenantService.shopAddressDisplay ?? '',
        );
        this.restaurantPhone = this.htmlDecoder.decode(
          this.tenantService.shopPhoneDisplay ?? '',
        );
        this.allergensNote = `Ask about ingredients, allergen information, production methods, or cooking methods before placing your order.`;
        this.setTitle();

        this.analytics.setTenant({
          tenant_name: (cfg as any).tenant?.companyName,
          tenant_domain: window.location.hostname,
        });
      });

    this.menuService.getPublicDeliveryRules().subscribe({
      next: (res) => {
        if (res?.status === 'success' && res?.deliveryRules) {
          this.fees = {
            minOrder: Number(res.deliveryRules.minimumDeliveryAmount ?? 0),
            deliveryFee: Number(res.deliveryRules.deliveryCharge ?? 0),
            serviceFee: this.tenantService.serviceFee ?? 0,
          };
        } else {
          this.fees = {
            minOrder: 0,
            deliveryFee: 0,
            serviceFee: this.tenantService.serviceFee ?? 0,
          };
        }
      },
      error: () => {
        this.fees = {
          minOrder: 0,
          deliveryFee: 0,
          serviceFee: this.tenantService.serviceFee ?? 0,
        };
      },
    });
  }

  toggleCart() {
    this.cartVisible = !this.cartVisible;
  }

  handleCheckout() {
    this.router.navigate(['/checkout']);
  }

  private setTitle() {
    this.title.setTitle(this.htmlDecoder.decode(this.tenantName || 'Waitron'));
  }
}
